import { Injectable } from '@angular/core';
import { LSKey } from '@app/enums/localStorageKey.enum';
import { Pet } from '@app/models/pet.model';
import { BehaviorSubject, catchError, Observable, of, tap } from 'rxjs';
import { PetApiService } from './api/pet-api.service';
import { APPOINTMENT_REQUEST, LocalStorageService, PET_LIST } from './local-storage.service';

@Injectable({
    providedIn: 'root',
})
export class PetService {
    private petListSubject = new BehaviorSubject<Pet[] | null>(null);
    public petList$ = this.petListSubject.asObservable();
    constructor(
        private petApiService: PetApiService,
        private localStorageService: LocalStorageService
    ) {}

    private normalizePetBreed(pets: Pet[]): Pet[] {
        for (const pet of pets) {
            if (!pet.petBreed && pet.petType?.petBreedList?.length) {
                pet.petBreed = pet.petType.petBreedList[0];
            }
        }
        return pets;
    }

    public getPetTypeList() {
        return this.petApiService.getPetTypeList();
    }

    getPetList(
        daySmartAccountId: string,
        onlineID: string,
        forceClear: boolean = false,
        isUpdateOnlineIdCalled: boolean = false
    ): Observable<Pet[]> {
        if (isUpdateOnlineIdCalled) {
            const petList = this.getPetListFromLS();
            if (petList?.length) {
                return of(petList);
            }
        }

        if (forceClear) {
            this.localStorageService.remove(PET_LIST);
        }

        let petList: Pet[] = [];

        if (!this.localStorageService.has(PET_LIST)) {
            return this.petApiService.getPetList(daySmartAccountId, onlineID).pipe(
                tap((data) => {
                    const validPets = this.normalizePetBreed((data || []).filter((pet) => pet && pet.name));
                    this.localStorageService.removeNested(APPOINTMENT_REQUEST, LSKey.PetInfo);
                    this.localStorageService.set(PET_LIST, validPets);
                    petList = validPets;
                    this.petListSubject.next(validPets);
                }),
                catchError((error) => {
                    console.error('Error fetching pet list:', error);
                    // Update BehaviorSubject with empty array on error
                    this.petListSubject.next([]);
                    // Return an empty array on error
                    return of([] as Pet[]);
                })
            );
        } else {
            return of(this.getPetListFromLS());
        }
    }

    public updatePetList(newPets: Pet[]) {
        const validPets = (newPets || []).filter((pet) => pet && pet.name);
        this.localStorageService.set(PET_LIST, validPets);
        this.petListSubject.next(validPets);
    }

    public addPet(pet: Pet) {
        if (!pet || !pet.name) {
            return;
        }
        const currentPets = this.petListSubject.getValue() || [];
        currentPets?.push(pet);
        this.updatePetList(currentPets);
    }

    clearPetList(): void {
        if (this.localStorageService.has(PET_LIST)) {
            this.localStorageService.remove(PET_LIST);
        }
        // Reset BehaviorSubject to null to clear stale data
        this.petListSubject.next(null);
    }

    private getPetListFromLS() {
        const petList = this.normalizePetBreed(((this.localStorageService.get(PET_LIST) as Pet[]) || []).filter((pet) => pet && pet.name));
        this.localStorageService.removeNested(APPOINTMENT_REQUEST, LSKey.PetInfo);
        this.petListSubject.next(petList);
        return petList;
    }
}
